import {useState} from 'react'
import {
  type LayoutChangeEvent,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import {Path, Svg} from 'react-native-svg'
import {Trans, useLingui} from '@lingui/react/macro'

import {SLIDE_ACCENT} from '#/lib/slideTokens'
import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {PLAY_SURFACE} from '#/components/capsules/play/playSurface'
import {QuestionHeading} from '#/components/capsules/play/ThemeBits'
import {type SlidePlayProps} from '#/components/capsules/play/types'
import {
  useFlashBookPlayDesign,
  useFlashBookPlayPalette,
  useInkStroke,
  usePlayAccent,
  usePlayContentPadding,
  usePlayFooterStyle,
  usePlayOptionRowRhythm,
  usePlayOptionStyle,
  usePlayOptionTextStyle,
  usePlayPrimaryAction,
  usePlayRuling,
  usePlaySelectionMode,
  usePlayStackGap,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {useFlashBookInkSound} from '#/components/flashbooks/useFlashBookInkSound'
import {SLIDE_TYPE_META} from '#/types/capsule'

const ACCENT = SLIDE_ACCENT.matching

/** Where a measured element sits inside the block, in block coordinates. */
type Rect = {x: number; y: number; w: number; h: number}

/** A stable pseudo-random number in [-1, 1) for a seed and a slot. */
function wobble(seed: string, slot: number): number {
  let h = 2166136261
  const s = `${seed}#${slot}`
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return ((h >>> 0) % 2000) / 1000 - 1
}

/**
 * The stroke that joins one word to another.
 *
 * A straight line between two columns is a diagram; a learner matching a country
 * to its capital draws a slightly bowed stroke that starts where the writing
 * ends and lands a little wide of the word it reaches. `hand` adds that bow and
 * the overshoot at both ends — and the wobble is hashed from the pair's own id,
 * so the stroke is the same on every render instead of shivering as you read it.
 * On a theme whose answers are cards the join is a plain rule between them.
 */
function inkPath(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  seed: string,
  hand: boolean,
): string {
  if (!hand) return `M ${x1} ${y1} L ${x2} ${y2}`
  const dx = x2 - x1
  const bow = 6 + 4 * wobble(seed, 1)
  const sag = wobble(seed, 2) * 5
  return (
    `M ${x1 - 3} ${y1} ` +
    `C ${x1 + dx * 0.35} ${y1 + bow + sag}, ` +
    `${x2 - dx * 0.35} ${y2 - bow + sag}, ` +
    `${x2 + 3} ${y2}`
  )
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

export function MatchingSlidePlay({
  slide,
  onAnswer,
  showFeedback,
  primaryColor,
  retryKey,
}: SlidePlayProps) {
  const {t: l} = useLingui()
  const palette = useFlashBookPlayPalette()
  const padding = usePlayContentPadding(24)
  // The items wear the book's own hand, not the app's UI font.
  const answerText = usePlayOptionTextStyle()
  // A whole number of the page's rules between the question and the columns.
  const stackGap = usePlayStackGap(16)
  // Inside a book every selection affordance wears the book's own accent.
  const accent = usePlayAccent(ACCENT)
  // The action strip is page furniture: on ruled paper it is the button alone,
  // printed on the lines, a whole number of rules tall.
  const footerStyle = usePlayFooterStyle()
  // The action, in the page's own language: on paper it is ink, not a button.
  const action = usePlayPrimaryAction(49)
  // Where the theme marks with a highlighter, a pair is two words joined by a
  // stroke rather than two tinted cards, so the cards are given up entirely.
  const highlight = usePlaySelectionMode() === 'highlight'
  const ink = useInkStroke()
  const inkSound = useFlashBookInkSound()
  const step = usePlayRuling()
  // The cell wears the theme's own answer shape, and on ruled paper its rows
  // are written on the rules: a `minHeight` of two lines with the word centred
  // inside it put every pair — and every box measured after it — 11px off the
  // ruling, which is exactly the line of writing `usePlayOptionRowRhythm`
  // exists to put back.
  const optionStyle = usePlayOptionStyle()
  const rowRhythm = usePlayOptionRowRhythm()
  const pairs = slide.pairs ?? []

  // Scrambled right column (stable across renders — shuffle once)
  const [rightItems] = useState(() =>
    shuffle(pairs.map(p => ({id: p.id, label: p.right}))),
  )
  const [lastRetryKey, setLastRetryKey] = useState(retryKey)
  const [selectedLeft, setSelectedLeft] = useState<string | null>(null)
  // matched: map leftId -> rightId
  const [matched, setMatched] = useState<Record<string, string>>({})

  /**
   * A matching exercise is *joined by hand*: the learner draws a line from the
   * country to its capital. So the join is drawn rather than implied by two
   * tinted rows — which means the widget has to know where the words actually
   * are. Every cell and every word reports its own box, and the strokes are laid
   * over the columns in those coordinates.
   */
  const design = useFlashBookPlayDesign()
  // Ruled paper is written on with a pen, so there the stroke bows like one;
  // a theme of cards joins two of them with a clean rule instead.
  const handDrawn = design.block.mode === 'ruled'
  const [columnsBox, setColumnsBox] = useState({w: 0, h: 0})
  const [rects, setRects] = useState<Record<string, Rect>>({})

  const measure = (key: string) => (e: LayoutChangeEvent) => {
    const {x, y, width, height} = e.nativeEvent.layout
    setRects(prev => {
      const cur = prev[key]
      return cur &&
        cur.x === x &&
        cur.y === y &&
        cur.w === width &&
        cur.h === height
        ? prev
        : {...prev, [key]: {x, y, w: width, h: height}}
    })
  }

  if (retryKey !== lastRetryKey) {
    setLastRetryKey(retryKey)
    setSelectedLeft(null)
    setMatched({})
  }

  const handleLeftTap = (pairId: string) => {
    if (showFeedback) return
    setSelectedLeft(prev => (prev === pairId ? null : pairId))
  }

  const handleRightTap = (rightId: string) => {
    if (showFeedback || !selectedLeft) return
    // Un-match any existing binding to this right item
    const nextMatched = Object.fromEntries(
      Object.entries(matched).filter(([, v]) => v !== rightId),
    )
    nextMatched[selectedLeft] = rightId
    setMatched(nextMatched)
    setSelectedLeft(null)
  }

  /** Whether there is anything to check yet — the action is dead until then. */
  const canCheck = Object.keys(matched).length > 0

  const handleCheck = () => {
    const maxPoints = pairs.length
    const correctMatches = pairs.filter(p => matched[p.id] === p.id).length
    const points = correctMatches
    onAnswer({
      isCorrect: points === maxPoints,
      points,
      maxPoints,
      solutionText: pairs.map(p => `${p.left} → ${p.right}`).join(', '),
      explanation: slide.explanation,
      userAnswer: matched,
    })
  }

  const isLeftMatched = (pairId: string) => pairId in matched
  const isRightMatched = (rightId: string) =>
    Object.values(matched).includes(rightId)

  const getLeftColor = (pairId: string) => {
    if (showFeedback && pairId in matched) {
      return matched[pairId] === pairId ? palette.positive : palette.negative
    }
    if (selectedLeft === pairId) return accent
    if (isLeftMatched(pairId)) return accent + '88'
    return palette.border
  }

  /** The ink a drawn pair wears: the pen's own, then right or wrong on verdict. */
  const lineColor = (pairId: string) =>
    showFeedback
      ? matched[pairId] === pairId
        ? palette.positive
        : palette.negative
      : accent

  const getRightColor = (rightId: string) => {
    if (showFeedback && isRightMatched(rightId)) {
      // Find the left pair that matched this right
      const leftId = Object.entries(matched).find(([, v]) => v === rightId)?.[0]
      return leftId === rightId ? palette.positive : palette.negative
    }
    if (isRightMatched(rightId)) return accent + '88'
    return palette.border
  }

  return (
    <View style={ss.root}>
      <PlayScroller
        style={ss.scroll}
        contentContainerStyle={[ss.scrollContent, {padding, gap: stackGap}]}>
        <QuestionHeading label={SLIDE_TYPE_META[slide.type]?.label}>
          {slide.question}
        </QuestionHeading>

        <View
          style={ss.columns}
          onLayout={e =>
            setColumnsBox({
              w: e.nativeEvent.layout.width,
              h: e.nativeEvent.layout.height,
            })
          }>
          <View
            style={[ss.col, step > 0 && {gap: stackGap}]}
            onLayout={measure('colL')}>
            {pairs.map(p => (
              <TouchableOpacity
                key={p.id}
                onLayout={measure(`cellL:${p.id}`)}
                style={[
                  ss.item,
                  optionStyle,
                  rowRhythm,
                  highlight
                    ? ss.itemBare
                    : {
                        borderColor: getLeftColor(p.id),
                        backgroundColor: palette.surface,
                      },
                ]}
                onPress={() => {
                  inkSound.mark()
                  handleLeftTap(p.id)
                }}
                accessibilityRole="button"
                accessibilityLabel={p.left}
                accessibilityHint={undefined}>
                <Text
                  onLayout={measure(`wordL:${p.id}`)}
                  style={[ss.itemText, answerText, {color: palette.body}]}>
                  <Text
                    style={ink(
                      highlight &&
                        (isLeftMatched(p.id) || selectedLeft === p.id)
                        ? getLeftColor(p.id)
                        : null,
                    )}>
                    {p.left}
                  </Text>
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <View
            style={[ss.col, step > 0 && {gap: stackGap}]}
            onLayout={measure('colR')}>
            {rightItems.map(r => (
              <TouchableOpacity
                key={r.id}
                onLayout={measure(`cellR:${r.id}`)}
                style={[
                  ss.item,
                  optionStyle,
                  rowRhythm,
                  highlight
                    ? ss.itemBare
                    : {
                        borderColor: getRightColor(r.id),
                        backgroundColor: palette.surface,
                      },
                ]}
                onPress={() => {
                  inkSound.mark()
                  handleRightTap(r.id)
                }}
                accessibilityRole="button"
                accessibilityLabel={r.label}
                accessibilityHint={undefined}>
                <Text
                  onLayout={measure(`wordR:${r.id}`)}
                  style={[ss.itemText, answerText, {color: palette.body}]}>
                  <Text
                    style={ink(
                      highlight && isRightMatched(r.id)
                        ? getRightColor(r.id)
                        : null,
                    )}>
                    {r.label}
                  </Text>
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* The lines the learner has drawn, over the two columns. Never
              touchable: a matching exercise is joined by tapping the words, and
              an overlay that swallowed those taps would make it unplayable. */}
          {columnsBox.w > 0 && (
            <View style={StyleSheet.absoluteFill} pointerEvents="none">
              <Svg width={columnsBox.w} height={columnsBox.h}>
                {Object.entries(matched).map(([leftId, rightId]) => {
                  const colL = rects.colL
                  const colR = rects.colR
                  const cellL = rects[`cellL:${leftId}`]
                  const cellR = rects[`cellR:${rightId}`]
                  const wordL = rects[`wordL:${leftId}`]
                  const wordR = rects[`wordR:${rightId}`]
                  // One frame's worth of not-yet-measured: nothing to draw.
                  if (!colL || !colR || !cellL || !cellR || !wordL || !wordR)
                    return null
                  // From the last letter of one word to the first of the other:
                  // the pen starts where the writing stops, not at the column's
                  // edge, which is what makes the join read as a match.
                  const x1 = colL.x + cellL.x + wordL.x + wordL.w
                  const y1 = colL.y + cellL.y + wordL.y + wordL.h / 2
                  const x2 = colR.x + cellR.x + wordR.x
                  const y2 = colR.y + cellR.y + wordR.y + wordR.h / 2
                  return (
                    <Path
                      key={leftId}
                      d={inkPath(x1, y1, x2, y2, leftId, handDrawn)}
                      stroke={lineColor(leftId)}
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  )
                })}
              </Svg>
            </View>
          )}
        </View>
      </PlayScroller>

      {!showFeedback && (
        <View
          style={[ss.footer, {borderTopColor: palette.border}, footerStyle]}>
          <TouchableOpacity
            style={[
              ss.checkBtn,
              {backgroundColor: canCheck ? primaryColor : palette.surfaceAlt},
              action.style,
            ]}
            onPress={handleCheck}
            disabled={!canCheck}
            accessibilityRole="button"
            accessibilityLabel={l`Check answer`}
            accessibilityHint={undefined}>
            <Text
              style={[
                ss.checkBtnText,
                {
                  color:
                    action.ink(canCheck) ??
                    (canCheck ? palette.onAccent : palette.muted),
                },
                action.labelStyle(canCheck),
              ]}>
              <Trans>Check</Trans>
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  )
}

const ss = StyleSheet.create({
  root: {flex: 1},
  scroll: {flex: 1},
  scrollContent: {padding: 24, gap: 16},
  question: {
    fontSize: 20,
    fontWeight: '700',
    color: PLAY_SURFACE.text,
    lineHeight: 28,
    marginTop: 8,
  },
  columns: {flexDirection: 'row', gap: 12, marginTop: 4},
  col: {flex: 1, gap: 10},
  item: {
    padding: 14,
    borderRadius: 10,
    borderWidth: 1.5,
    backgroundColor: PLAY_SURFACE.surface,
    minHeight: 52,
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemText: {
    fontSize: 14,
    fontWeight: '600',
    color: PLAY_SURFACE.text,
    textAlign: 'center',
    // A pair is one column wide, and a long one has to wrap rather than push
    // its column — and the ruling the other column is written on — sideways.
    flexShrink: 1,
  },
  /**
   * The cell without its card: the word alone, on the paper.
   *
   * The word stays centred in its column — the two columns are a list of pairs,
   * and a left-aligned right column would read as a different kind of thing.
   */
  itemBare: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderRadius: 0,
    padding: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  explanationBox: {
    padding: 14,
    backgroundColor: PLAY_SURFACE.surface,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: PLAY_SURFACE.border,
  },
  explanationText: {
    fontSize: 14,
    color: PLAY_SURFACE.textMuted,
    lineHeight: 20,
  },
  footer: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: PLAY_SURFACE.border,
  },
  checkBtn: {borderRadius: 14, paddingVertical: 15, alignItems: 'center'},
  checkBtnText: {color: '#fff', fontSize: 16, fontWeight: '700'},
  checkBtnDisabled: {color: 'rgba(255,255,255,0.4)'},
})
